import { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * Image upload widget — uploads to /api/admin/upload, returns the public URL via onChange.
 * Falls back to URL paste mode when needed.
 */
export default function ImageUpload({ value, onChange, label = "Image", testId = "image-upload" }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [mode, setMode] = useState("upload"); // 'upload' | 'url'

    const handleFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please choose an image file");
            return;
        }
        const form = new FormData();
        form.append("file", file);
        setUploading(true);
        try {
            const { data } = await api.post("/admin/upload", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(data.url);
            toast.success("Image uploaded");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div data-testid={testId}>
            <div className="mb-2 flex items-center justify-between gap-2">
                <label className="text-[10px] uppercase tracking-[0.25em] text-white/50">{label}</label>
                <div className="flex gap-1 text-[10px] uppercase tracking-widest">
                    <button
                        type="button"
                        onClick={() => setMode("upload")}
                        className={`px-2 py-1 transition-colors ${mode === "upload" ? "text-gold" : "text-white/40 hover:text-white"}`}
                    >
                        Upload
                    </button>
                    <span className="text-white/20">·</span>
                    <button
                        type="button"
                        onClick={() => setMode("url")}
                        className={`px-2 py-1 transition-colors ${mode === "url" ? "text-gold" : "text-white/40 hover:text-white"}`}
                    >
                        URL
                    </button>
                </div>
            </div>

            {value && (
                <div className="relative mb-3 inline-block">
                    <img src={value} alt="" className="h-24 w-24 border border-border-dim object-cover" />
                    <button
                        type="button"
                        onClick={() => onChange("")}
                        data-testid={`${testId}-clear`}
                        className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-border-dim bg-obsidian text-white hover:border-heart hover:text-heart"
                    >
                        <X className="h-3 w-3" />
                    </button>
                </div>
            )}

            {mode === "upload" ? (
                <div
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                        e.preventDefault();
                        if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
                    }}
                    className="flex cursor-pointer items-center justify-center gap-2 border border-dashed border-border-dim bg-transparent px-4 py-3 text-xs text-white/60 transition-colors hover:border-gold hover:text-gold"
                >
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    <span>{uploading ? "Uploading…" : "Click or drag to upload"}</span>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid={`${testId}-file`}
                        onChange={(e) => handleFile(e.target.files?.[0])}
                    />
                </div>
            ) : (
                <input
                    type="text"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="https://…"
                    data-testid={`${testId}-url`}
                    className="w-full border border-border-dim bg-transparent px-3 py-2 text-sm text-white outline-none focus:border-gold"
                />
            )}
        </div>
    );
}
